// Утилиты для работы с производственным календарем России

// Фиксированные праздничные дни России (месяц-1, день)
const FIXED_HOLIDAYS = [
  [0, 1], [0, 2], [0, 3], [0, 4], [0, 5], [0, 6], [0, 7], [0, 8], // 1-8 января
  [1, 23], // 23 февраля
  [2, 8],  // 8 марта
  [4, 1],  // 1 мая
  [4, 9],  // 9 мая
  [5, 12], // 12 июня
  [10, 4]  // 4 ноября
];

// Кэш для хранения данных календаря
let calendarCache = {};

/**
 * Преобразует строку с данными за весь год в карту "YYYY-MM" → массив дней.
 */
const sliceYearData = (year, rawData) => {
  if (!rawData || rawData.length === 0) {
    return null;
  }

  const monthsInYear = [31, (new Date(year, 1, 29).getDate() === 29 ? 29 : 28), 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let pointer = 0;
  const sliced = {};

  for (let month = 0; month < 12; month++) {
    const daysInMonth = monthsInYear[month];
    const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

    const segment = rawData.slice(pointer, pointer + daysInMonth);
    if (segment.length === daysInMonth) {
      sliced[monthKey] = segment.split('').map(Number);
    } else {
      // Если данных не хватает, используем генератор заглушки
      sliced[monthKey] = generatePreliminaryCalendar(year, month);
    }
    pointer += daysInMonth;
  }

  return sliced;
};

const getMonthFromCache = (year, month) => {
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  return calendarCache[monthKey] || null;
};

const saveMonthToCache = (year, month, data) => {
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  calendarCache[monthKey] = data;
};

// Генерация предварительного календаря для будущих лет
const generatePreliminaryCalendar = (year, month) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthData = [];
  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    
    // Проверяем, является ли день выходным
    let isHoliday = false;
    
    // Суббота или воскресенье
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      isHoliday = true;
    }
    
    // Проверяем фиксированные праздники
    for (const [holidayMonth, holidayDay] of FIXED_HOLIDAYS) {
      if (month === holidayMonth && day === holidayDay) {
        isHoliday = true;
        break;
      }
    }
    
    // 0 - рабочий день, 1 - выходной
    monthData.push(isHoliday ? 1 : 0);
  }
  
  return monthData;
};

// Загрузка данных календаря для конкретного месяца
const loadCalendarMonth = async (year, month) => {
  const monthStr = String(month + 1).padStart(2, '0');
  const monthKey = `${year}-${monthStr}`;

  const cached = getMonthFromCache(year, month);
  if (cached) {
    return cached;
  }
  
  try {
    // Загружаем данные. API может вернуть месяц (31 символ) или весь год (365 символов)
    const url = `https://isdayoff.ru/api/getdata?year=${year}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      // Если данных нет, генерируем предварительный календарь
      const preliminaryData = generatePreliminaryCalendar(year, month);
      saveMonthToCache(year, month, preliminaryData);
      return preliminaryData;
    }
    
    const data = await response.text();
    const isAllZeros = data && /^0+$/.test(data);

    if (!data || data.length === 0 || data.includes('error') || data.includes('Error') || isAllZeros) {
      const preliminaryData = generatePreliminaryCalendar(year, month);
      saveMonthToCache(year, month, preliminaryData);
      return preliminaryData;
    }

    const expectedLength = new Date(year, month + 1, 0).getDate();
    if (data.length === expectedLength) {
      const monthData = data.split('').map(Number);
      saveMonthToCache(year, month, monthData);
      return monthData;
    }

    if (data.length >= 365) {
      const slicedYear = sliceYearData(year, data);
      if (slicedYear) {
        Object.entries(slicedYear).forEach(([key, value]) => {
          calendarCache[key] = value;
        });
        return calendarCache[monthKey];
      }
    }

    // Fallback: предварительный календарь
    const preliminaryData = generatePreliminaryCalendar(year, month);
    saveMonthToCache(year, month, preliminaryData);
    return preliminaryData;
    
  } catch (error) {
    console.warn('Ошибка загрузки календаря для', monthKey, ':', error);
    // В случае ошибки используем предварительный календарь
    const preliminaryData = generatePreliminaryCalendar(year, month);
    saveMonthToCache(year, month, preliminaryData);
    return preliminaryData;
  }
};

// Проверка, является ли день рабочим
const isWorkingDay = async (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  
  try {
    const monthData = await loadCalendarMonth(year, month);
    // monthData[day - 1]: 0 - рабочий день, 1 - выходной
    return monthData[day - 1] === 0;
  } catch (error) {
    console.warn('Ошибка проверки рабочего дня:', error);
    // Fallback: используем простую логику (пн-пт рабочие дни)
    const dayOfWeek = date.getDay();
    return dayOfWeek >= 1 && dayOfWeek <= 5;
  }
};

// Подсчет рабочих дней между двумя датами (исключая начальную дату)
const calculateWorkingDays = async (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (end <= start) return 0;
  
  let workingDays = 0;
  const current = new Date(start);
  current.setDate(current.getDate() + 1); // Начинаем со следующего дня после startDate
  
  // Группируем даты по месяцам для оптимизации загрузки календаря
  const monthsToLoad = new Set();
  const tempDate = new Date(current);
  
  while (tempDate <= end) {
    const monthKey = `${tempDate.getFullYear()}-${tempDate.getMonth()}`;
    monthsToLoad.add(monthKey);
    tempDate.setMonth(tempDate.getMonth() + 1);
    tempDate.setDate(1);
  }
  
  // Предзагружаем данные календаря для всех нужных месяцев
  const calendarPromises = Array.from(monthsToLoad).map(monthKey => {
    const [year, month] = monthKey.split('-').map(Number);
    return loadCalendarMonth(year, month);
  });
  
  await Promise.all(calendarPromises);
  
  // Теперь подсчитываем рабочие дни (не включая начальную дату)
  while (current <= end) {
    if (await isWorkingDay(current)) {
      workingDays++;
    }
    current.setDate(current.getDate() + 1);
  }
  
  return workingDays;
};

// Добавление рабочих дней к дате
const addWorkingDays = async (startDate, daysToAdd) => {
  if (!startDate || !daysToAdd || daysToAdd <= 0) return null;
  
  const result = new Date(startDate);
  let addedDays = 0;
  
  // Предзагружаем календарь на несколько месяцев вперед
  const monthsToPreload = Math.ceil(daysToAdd / 15) + 2; // Более умный расчет предзагрузки
  const preloadPromises = [];
  
  for (let i = 0; i < monthsToPreload; i++) {
    const preloadDate = new Date(result);
    preloadDate.setMonth(preloadDate.getMonth() + i);
    preloadPromises.push(loadCalendarMonth(preloadDate.getFullYear(), preloadDate.getMonth()));
  }
  
  await Promise.all(preloadPromises);
  
  let attempts = 0;
  const maxAttempts = daysToAdd * 3; // Максимум попыток для предотвращения бесконечного цикла
  
  while (addedDays < daysToAdd && attempts < maxAttempts) {
    result.setDate(result.getDate() + 1);
    attempts++;
    
    if (await isWorkingDay(result)) {
      addedDays++;
    }
  }
  
  if (attempts >= maxAttempts) {
    console.warn('Превышен лимит попыток поиска рабочих дней');
  }
  
  return result;
};

// Поиск ближайшего рабочего дня (если дата попадает на выходной)
const findNextWorkingDay = async (date) => {
  const result = new Date(date);
  
  // Предзагружаем календарь на месяц вперед
  await loadCalendarMonth(result.getFullYear(), result.getMonth());
  
  let attempts = 0;
  while (attempts < 14) { // Максимум 2 недели поиска
    if (await isWorkingDay(result)) {
      return result;
    }
    result.setDate(result.getDate() + 1);
    attempts++;
  }
  
  // Если не нашли рабочий день за 2 недели, возвращаем исходную дату
  return new Date(date);
};

// Функция для тестирования алгоритмов (отключена в продакшене)
const testWorkingDaysCalculation = async () => {
  // Тестирование отключено для продакшена
  return;
};

export {
  isWorkingDay,
  calculateWorkingDays,
  addWorkingDays,
  findNextWorkingDay,
  loadCalendarMonth,
  testWorkingDaysCalculation
};
